import React, { useMemo } from "react";

import { GlobeIcon, GoogleIcon, SpinnerIcon } from "@/components/icons";

import {
  useGoogleReport,
  type AdsTotals,
  type Ga4Totals,
  type GbpTotals,
  type GoogleAdsCampaignRow,
  type GoogleAdsReport,
  type GoogleBusinessProfileReport,
  type GooglePace,
  type GoogleSourceWindow,
  type GoogleToDate,
  type GoogleWebsiteReport,
  type GoogleWindowReport,
} from "../hooks/useGoogleReport";
import type { ReportPeriod } from "../utils/period";
import {
  formatDayMonth,
  formatEuroMinor,
  formatPoints,
  formatWindows,
  isAfterDay,
  metricChange,
  MONTH_SHORT,
  pointsChange,
} from "../utils/metricChange";
import {
  addDays,
  formatRange,
  formatRanges,
  formatShortDay,
  monthRange,
  type DayRange,
} from "../utils/dayAxis";
import {
  ChangePill,
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import {
  BarList,
  QuietList,
  SOURCE_COLOR,
  SourceBadge,
  Sparkline,
  SplitBar,
  VizHeading,
  type QuietRow,
} from "./ReportViz";
import { PaceChart } from "./PaceChart";
import { dayPips, type DayPip } from "./DayPips";
import {
  campaignEvents,
  campaignLanes,
  campaignsByDay,
  type CampaignSpan,
} from "../utils/dayMarkers";

interface GoogleSectionProps {
  clientId: string;
  /** A month, or the last 7 or 28 days. */
  period: ReportPeriod;
  /** Today in Rome: a window's request keys on it. */
  today: string;
  /** The one source's panel to draw: a Reporting canvas tile or rail item. */
  only: GoogleSource;
}

export type GoogleSource = "businessProfile" | "website" | "googleAds";

const TITLE: Record<GoogleSource, string> = {
  businessProfile: "Business Profile",
  website: "Website",
  googleAds: "Google Ads",
};

const ICON = {
  businessProfile: (
    <SourceBadge color={SOURCE_COLOR.businessProfile}>
      <GoogleIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  ),
  website: (
    <SourceBadge color={SOURCE_COLOR.website}>
      <GlobeIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  ),
  googleAds: (
    <SourceBadge color={SOURCE_COLOR.paid}>
      <GoogleIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  ),
};

/** A Google panel of /reporting, the one `only` names: Business Profile,
 *  Website (GA4) or Google Ads for the month, each led by its pace chart,
 *  then its two or three key figures against the month before, or on a
 *  month in progress against the same days of it. Folded, each shows its
 *  headline figure and a sparkline of it by day.
 *
 *  In window mode each source reads its own window, ending on its settled
 *  day, against the window before it (see the window panels below).
 *
 *  One fetch, cached per client and month (or window and day), serves all
 *  three panels. A panel never hides: an unlinked source names the missing
 *  link, a linked one with no rows says so. */
export const GoogleSection: React.FC<GoogleSectionProps> = ({
  clientId,
  period,
  today,
  only,
}) => {
  const { data, isLoading, error } = useGoogleReport(clientId, period, today);
  // The period's Google Ads campaigns by cost: lanes under the Business
  // Profile and Website charts, start and end pips on the Ads chart.
  const campaigns = useMemo<CampaignSpan[]>(
    () =>
      (data?.report.googleAds?.campaigns ?? []).map((c) => ({
        key: c.campaignId,
        name: c.name,
        startDate: c.startDate,
        endDate: c.endDate,
        days: c.daily.map((d) => d.date),
        channelType: c.channelType,
      })),
    [data],
  );
  // The Ads chart's days: the month, or the Ads source's own span.
  const adsRange = useMemo((): DayRange | null => {
    if (!data) return null;
    if (data.kind === "month") return monthRange(data.report.year, data.report.month);
    return data.report.googleAds?.span ?? null;
  }, [data]);
  const pips = useMemo(
    () => (adsRange ? dayPips([], campaignsByDay(campaignEvents(campaigns), adsRange)) : []),
    [campaigns, adsRange],
  );

  if (isLoading || error || !data) {
    const loadingRow = (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
        <SpinnerIcon className="w-4 h-4" />
        Loading Google data…
      </div>
    );
    const body = isLoading ? (
      loadingRow
    ) : (
      <ReportPanelEmpty
        message="Google data is unavailable."
        hint={`Could not load the Google report for ${period.kind === "month" ? "this month" : "these days"}.`}
      />
    );
    const folded = isLoading ? (
      loadingRow
    ) : (
      <ReportPanelNote>Google data is unavailable.</ReportPanelNote>
    );
    return (
      <ReportPanel title={TITLE[only]} icon={ICON[only]} collapsedBody={folded}>
        {body}
      </ReportPanel>
    );
  }

  if (data.kind === "window") {
    const report = data.report;
    switch (only) {
      case "businessProfile":
        return <BusinessProfileWindowPanel gbp={report.businessProfile} campaigns={campaigns} />;
      case "website":
        return <WebsiteWindowPanel website={report.website} campaigns={campaigns} />;
      case "googleAds":
        return <GoogleAdsWindowPanel ads={report.googleAds} campaigns={campaigns} pips={pips} />;
    }
  }
  const { year, month } = data.report;
  switch (only) {
    case "businessProfile":
      return <BusinessProfilePanel gbp={data.report.businessProfile} year={year} month={month} campaigns={campaigns} />;
    case "website":
      return <WebsitePanel website={data.report.website} year={year} month={month} campaigns={campaigns} />;
    case "googleAds":
      return (
        <GoogleAdsPanel ads={data.report.googleAds} year={year} month={month} campaigns={campaigns} pips={pips} />
      );
  }
};

/** "partial month · final from 5 November" on a month the source has not
 *  settled. Not fetchedThrough: that is the sync night, not the data day. */
const partialSummary = (finalFrom: string): string =>
  `partial month · final from ${formatDayMonth(finalFrom)}`;

/** Header summary for a source: an unlinked account's history first, then
 *  a source past its finalFrom that is still not complete (its sync is
 *  behind), else the partial-month note while the month settles. A month
 *  with same-days windows names them first, since its figures compare them. */
const sourceSummary = (source: {
  linked: boolean;
  complete: boolean;
  finalFrom: string;
  fetchedThrough: string | null;
  toDate: { through: string; previousThrough: string; current: unknown } | null;
}): string | undefined => {
  const windows =
    !source.complete && source.toDate?.current
      ? formatWindows(source.toDate.through, source.toDate.previousThrough)
      : null;
  if (!source.linked) {
    return windows ? `previously linked account · ${windows}` : "previously linked account";
  }
  if (source.complete) return undefined;
  const status = isAfterDay(source.finalFrom)
    ? source.fetchedThrough
      ? `sync behind · last fetched ${formatDayMonth(source.fetchedThrough)}`
      : "sync behind"
    : partialSummary(source.finalFrom);
  return windows ? `${windows} · ${status}` : status;
};

/** What a panel's figures compare: the month against the month before once
 *  it is complete; on a month in progress the same-days windows, so a part
 *  month never reads as a fall. With no window rows yet, values and no
 *  change. */
const comparedPair = <T,>(
  complete: boolean,
  current: T,
  previous: T | null,
  toDate: GoogleToDate<T> | null,
): { shown: T; base: T | null } => {
  if (complete) return { shown: current, base: previous };
  if (toDate?.current) return { shown: toDate.current, base: toDate.previous };
  return { shown: current, base: null };
};

/** A source's headline metric over its same-days windows, for the pace
 *  chart's label. */
const headlineToDate = <T,>(
  toDate: GoogleToDate<T> | null,
  read: (totals: T) => number,
): { through: string; current: number | null; previous: number | null } | null =>
  toDate
    ? {
        through: toDate.through,
        current: toDate.current ? read(toDate.current) : null,
        previous: toDate.previous ? read(toDate.previous) : null,
      }
    : null;

/** The headline metric per day, from the pace's running total, one point a
 *  day from the period's first. */
const perDay = (pace: GooglePace): Array<number | null> =>
  pace.current.map((p, i) => p.value - (i === 0 ? 0 : pace.current[i - 1].value));

interface PanelMonth {
  year: number;
  month: number;
  /** The month's Google Ads campaigns, by cost. */
  campaigns: ReadonlyArray<CampaignSpan>;
}

/** The month's axis for a pace chart: its days, the month before's, and
 *  their names. */
const monthAxis = (year: number, month: number) => {
  const previous = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
  return {
    range: monthRange(year, month),
    previousRange: monthRange(previous.year, previous.month),
    labels: { current: MONTH_SHORT[month - 1], previous: MONTH_SHORT[previous.month - 1] },
  };
};

/** A source's last settled day of the range: the range's last once it is
 *  complete, else its pace's last day (the day before the range before
 *  any). */
const settledOf = (source: { complete: boolean; pace: GooglePace }, range: DayRange): string =>
  source.complete
    ? range.until
    : source.pace.current[source.pace.current.length - 1]?.date ?? addDays(range.since, -1);

/** The campaigns' lanes, clipped to the source's settled day, and the same
 *  days as 2px lines under its folded sparkline. */
const campaignMarks = (
  campaigns: ReadonlyArray<CampaignSpan>,
  source: { complete: boolean; pace: GooglePace },
  range: DayRange,
) => {
  const lanes = campaignLanes(campaigns, range, settledOf(source, range));
  const spans = lanes.lanes.map((lane) => ({ key: lane.key, from: lane.from, to: lane.to }));
  return { lanes, spans };
};

const NOT_LINKED: Record<GoogleSource, { message: string; hint: string }> = {
  businessProfile: {
    message: "No Business Profile linked.",
    hint: "Link the client's Google Business Profile location to see Maps and Search stats.",
  },
  website: {
    message: "No Google Analytics property linked.",
    hint: "Link the client's GA4 property to see website sessions and key events.",
  },
  googleAds: {
    message: "No Google Ads account linked.",
    hint: "Set the client's Google Ads customer ID in its settings.",
  },
};

/** A source the client has no account for, in either mode. */
const NotLinked: React.FC<{ source: GoogleSource }> = ({ source }) => (
  <ReportPanel
    title={TITLE[source]}
    icon={ICON[source]}
    collapsedBody={<ReportPanelNote>{NOT_LINKED[source].message}</ReportPanelNote>}
  >
    <ReportPanelEmpty message={NOT_LINKED[source].message} hint={NOT_LINKED[source].hint} />
  </ReportPanel>
);

/** A source with no rows for the month. An unlinked account gets no new
 *  sync, so it never says the rows are on their way. */
const noRowsNote = (linked: boolean) => (
  <ReportPanelNote>{linked ? "No data for this month yet." : "No data for this month."}</ReportPanelNote>
);

const NoRows: React.FC<{ linked: boolean }> = ({ linked }) =>
  linked ? (
    <ReportPanelEmpty
      message="No data for this month yet."
      hint="Rows appear once the daily Google sync covers this month."
    />
  ) : (
    <ReportPanelEmpty
      message="No data for this month."
      hint="This panel shows history from a previously linked account; no new data will sync."
    />
  );

const HistoryNote: React.FC = () => (
  <p className="text-xs text-amber-600">
    Showing history from a previously linked account; no new data will sync.
  </p>
);

/** A demoted figure with its change pill, for a panel's quiet list. */
const quietRow = (
  label: string,
  value: string,
  change: { value: number; percentage: number | null } | undefined,
  formatChange?: (value: number) => string,
  trend?: "up" | "down" | "neutral",
): QuietRow => ({
  label,
  value,
  aside: change ? <ChangePill change={change} formatChange={formatChange} trend={trend} /> : undefined,
});

const BusinessProfilePanel: React.FC<PanelMonth & { gbp: GoogleBusinessProfileReport | null }> = ({
  gbp,
  year,
  month,
  campaigns,
}) => {
  const icon = ICON.businessProfile;
  if (!gbp) return <NotLinked source="businessProfile" />;
  const summary = sourceSummary(gbp);
  if (!gbp.current) {
    return (
      <ReportPanel
        title="Business Profile"
        icon={icon}
        summary={summary}
        collapsedBody={noRowsNote(gbp.linked)}
      >
        <NoRows linked={gbp.linked} />
      </ReportPanel>
    );
  }
  const { shown: cur, base: prev } = comparedPair(
    gbp.complete,
    gbp.current,
    gbp.previous,
    gbp.toDate,
  );
  const marks = campaignMarks(campaigns, gbp, monthRange(year, month));
  return (
    <BusinessProfileView
      linked={gbp.linked}
      summary={summary}
      cur={cur}
      prev={prev}
      pace={gbp.pace}
      spans={marks.spans}
      frame={{
        complete: gbp.complete,
        toDate: headlineToDate(gbp.toDate, (totals) => totals.actions),
        ...monthAxis(year, month),
        lanes: marks.lanes,
      }}
      keywords={gbp.keywords}
    />
  );
};

/** What a pace chart is drawn on beside its pace: the period's days, the
 *  days before, their names, how complete the period is, and the lanes. */
type PaceFrame = Pick<
  React.ComponentProps<typeof PaceChart>,
  "range" | "previousRange" | "labels" | "complete" | "toDate" | "lanes"
>;

/** What a Google panel draws once it has figures, the same in Month and
 *  window mode: the shown totals, the ones they compare with (null for no
 *  change), the pace chart's frame and the campaign spans under the folded
 *  sparkline. */
interface SourceView<T> {
  linked: boolean;
  summary: string | undefined;
  cur: T;
  prev: T | null;
  pace: GooglePace;
  spans: ReadonlyArray<{ key: string; from: number; to: number }>;
  frame: PaceFrame;
}

const BusinessProfileView: React.FC<
  SourceView<GbpTotals> & {
    /** The month's top keywords; null in window mode, where Google has none. */
    keywords: GoogleBusinessProfileReport["keywords"] | null;
  }
> = ({ linked, summary, cur, prev, pace, spans, frame, keywords }) => {
  const color = SOURCE_COLOR.businessProfile;
  const actionRows = [
    quietRow("Calls", cur.callClicks.toLocaleString(), metricChange(cur.callClicks, prev?.callClicks)),
    quietRow(
      "Directions",
      cur.directionRequests.toLocaleString(),
      metricChange(cur.directionRequests, prev?.directionRequests),
    ),
    quietRow(
      "Website clicks",
      cur.websiteClicks.toLocaleString(),
      metricChange(cur.websiteClicks, prev?.websiteClicks),
    ),
    ...(cur.bookings > 0
      ? [quietRow("Bookings", cur.bookings.toLocaleString(), metricChange(cur.bookings, prev?.bookings))]
      : []),
    ...(cur.foodOrders > 0
      ? [
          quietRow(
            "Food orders",
            cur.foodOrders.toLocaleString(),
            metricChange(cur.foodOrders, prev?.foodOrders),
          ),
        ]
      : []),
  ];

  return (
    <ReportPanel
      title="Business Profile"
      icon={ICON.businessProfile}
      summary={summary}
      headlineLabel="Actions"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Actions"
              value={cur.actions.toLocaleString()}
              change={metricChange(cur.actions, prev?.actions)}
            />
          }
          chart={
            <Sparkline
              values={perDay(pace)}
              color={color}
              title="Actions per day"
              spans={spans}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {!linked && <HistoryNote />}
        <PaceChart pace={pace} label="Actions" color={color} {...frame} />
        <ReportStats>
          <ReportStat
            label="Actions"
            value={cur.actions.toLocaleString()}
            change={metricChange(cur.actions, prev?.actions)}
          />
          <ReportStat
            label="Impressions"
            value={cur.impressions.toLocaleString()}
            change={metricChange(cur.impressions, prev?.impressions)}
          />
        </ReportStats>
        <QuietList rows={actionRows} />
        {cur.impressions > 0 && (
          <div>
            <VizHeading>Impressions, Maps vs Search</VizHeading>
            <SplitBar
              parts={[
                { label: "Maps", value: cur.impressionsMaps, color },
                { label: "Search", value: cur.impressionsSearch, color: "#A7F3D0" },
              ]}
            />
          </div>
        )}
        {keywords == null ? (
          <ReportPanelNote>Search keywords are reported by month.</ReportPanelNote>
        ) : (
          keywords.length > 0 && (
            <div>
              <VizHeading>Top search keywords</VizHeading>
              <BarList
                color={color}
                rows={keywords.map((k) => {
                  const hidden = k.impressions == null || k.belowThreshold;
                  return {
                    key: k.keyword,
                    label: k.keyword,
                    value: hidden ? null : k.impressions,
                    // Google withholds counts below its reporting threshold.
                    display: hidden ? "low" : (k.impressions ?? 0).toLocaleString(),
                  };
                })}
              />
            </div>
          )
        )}
      </div>
    </ReportPanel>
  );
};

// GA4's paid channel groups, drawn amber so paid stays apart from organic.
const PAID_CHANNEL = /^(Paid |Display$|Cross-network$)/;

const WebsitePanel: React.FC<PanelMonth & { website: GoogleWebsiteReport | null }> = ({
  website,
  year,
  month,
  campaigns,
}) => {
  const icon = ICON.website;
  if (!website) return <NotLinked source="website" />;
  const summary = sourceSummary(website);
  const monthTotals = website.current;
  if (!monthTotals) {
    return (
      <ReportPanel
        title="Website"
        icon={icon}
        summary={summary}
        collapsedBody={noRowsNote(website.linked)}
      >
        <NoRows linked={website.linked} />
      </ReportPanel>
    );
  }
  const { shown: cur, base: prev } = comparedPair(
    website.complete,
    monthTotals,
    website.previous,
    website.toDate,
  );
  const marks = campaignMarks(campaigns, website, monthRange(year, month));
  // Users do not add across days, so a same-days window has none: the users
  // rows keep the month so far, with no change.
  const users = cur.totalUsers ?? monthTotals.totalUsers;
  const newUsers = cur.newUsers ?? monthTotals.newUsers;
  const soFar = cur.totalUsers == null && monthTotals.totalUsers != null ? " so far" : "";
  return (
    <WebsiteView
      linked={website.linked}
      summary={summary}
      cur={cur}
      prev={prev}
      pace={website.pace}
      spans={marks.spans}
      frame={{
        complete: website.complete,
        toDate: headlineToDate(website.toDate, (totals) => totals.sessions),
        ...monthAxis(year, month),
        lanes: marks.lanes,
      }}
      users={[
        quietRow(
          `Users${soFar}`,
          users != null ? users.toLocaleString() : "-",
          metricChange(cur.totalUsers, prev?.totalUsers),
        ),
        quietRow(
          `New users${soFar}`,
          newUsers != null ? newUsers.toLocaleString() : "-",
          metricChange(cur.newUsers, prev?.newUsers),
        ),
      ]}
      channels={website.channels}
      keyEvents={website.keyEvents}
    />
  );
};

const WebsiteView: React.FC<
  SourceView<Ga4Totals> & {
    /** The users rows; null in window mode, where users are monthly. */
    users: QuietRow[] | null;
    channels: GoogleWebsiteReport["channels"];
    keyEvents: GoogleWebsiteReport["keyEvents"];
  }
> = ({ linked, summary, cur, prev, pace, spans, frame, users, channels, keyEvents }) => {
  const color = SOURCE_COLOR.website;
  const hasPaidChannel = channels.some((c) => PAID_CHANNEL.test(c.channelGroup));

  return (
    <ReportPanel
      title="Website"
      icon={ICON.website}
      summary={summary}
      headlineLabel="Sessions"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Sessions"
              value={cur.sessions.toLocaleString()}
              change={metricChange(cur.sessions, prev?.sessions)}
            />
          }
          chart={
            <Sparkline
              values={perDay(pace)}
              color={color}
              title="Sessions per day"
              spans={spans}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {!linked && <HistoryNote />}
        <PaceChart pace={pace} label="Sessions" color={color} {...frame} />
        <ReportStats>
          <ReportStat
            label="Sessions"
            value={cur.sessions.toLocaleString()}
            change={metricChange(cur.sessions, prev?.sessions)}
          />
          <ReportStat
            label="Key events"
            value={cur.keyEvents.toLocaleString()}
            change={metricChange(cur.keyEvents, prev?.keyEvents)}
          />
        </ReportStats>
        <QuietList
          rows={[
            ...(users ?? []),
            quietRow(
              "Engagement rate",
              cur.engagementRate != null ? `${(cur.engagementRate * 100).toFixed(1)}%` : "-",
              pointsChange(cur.engagementRate, prev?.engagementRate),
              formatPoints,
            ),
          ]}
        />
        {users == null && <ReportPanelNote>Users are counted by month.</ReportPanelNote>}
        {channels.length > 0 && (
          <div>
            <VizHeading
              aside={
                hasPaidChannel ? (
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: SOURCE_COLOR.paid }}
                    />
                    paid
                  </span>
                ) : undefined
              }
            >
              Sessions by channel
            </VizHeading>
            <ChannelBars channels={channels} color={color} />
          </div>
        )}
        {keyEvents.length > 0 && (
          <div>
            <VizHeading>Top key events</VizHeading>
            <BarList
              color={color}
              rows={keyEvents.map((e) => ({
                key: e.eventName,
                label: e.eventName,
                value: e.keyEvents,
                display: e.keyEvents.toLocaleString(),
              }))}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};

/** Channels by sessions, organic in the source colour and paid in amber. */
const ChannelBars: React.FC<{
  channels: GoogleWebsiteReport["channels"];
  color: string;
}> = ({ channels, color }) => (
  <BarList
    color={color}
    rows={channels.map((c) => ({
      key: c.channelGroup,
      label: c.channelGroup,
      value: c.sessions,
      display: c.sessions.toLocaleString(),
      color: PAID_CHANNEL.test(c.channelGroup) ? SOURCE_COLOR.paid : undefined,
    }))}
  />
);

const GoogleAdsPanel: React.FC<
  PanelMonth & { ads: GoogleAdsReport | null; pips: ReadonlyArray<DayPip> }
> = ({ ads, year, month, campaigns, pips }) => {
  const icon = ICON.googleAds;
  if (!ads) return <NotLinked source="googleAds" />;
  const summary = sourceSummary(ads);
  if (!ads.current) {
    return (
      <ReportPanel
        title="Google Ads"
        icon={icon}
        summary={summary}
        collapsedBody={noRowsNote(ads.linked)}
      >
        <NoRows linked={ads.linked} />
      </ReportPanel>
    );
  }
  const { shown: cur, base: prev } = comparedPair(
    ads.complete,
    ads.current,
    ads.previous,
    ads.toDate,
  );
  const marks = campaignMarks(campaigns, ads, monthRange(year, month));
  return (
    <GoogleAdsView
      linked={ads.linked}
      summary={summary}
      cur={cur}
      prev={prev}
      pace={ads.pace}
      spans={marks.spans}
      frame={{
        complete: ads.complete,
        toDate: headlineToDate(ads.toDate, (totals) => totals.clicks),
        ...monthAxis(year, month),
      }}
      campaigns={ads.campaigns}
      pips={pips}
    />
  );
};

const GoogleAdsView: React.FC<
  SourceView<AdsTotals> & { campaigns: GoogleAdsCampaignRow[]; pips: ReadonlyArray<DayPip> }
> = ({ linked, summary, cur, prev, pace, spans, frame, campaigns, pips }) => {
  const color = SOURCE_COLOR.paid;
  // Money deltas stay in cents and print as euros with the cents.
  const formatMinorDelta = (minor: number) => formatEuroMinor(minor, true);
  const cpcChange = metricChange(cur.cpcMinor, prev?.cpcMinor);
  // Lower CPC is the win: colour by favorability, the label keeps the raw delta.
  const cpcTrend = !cpcChange
    ? undefined
    : cpcChange.value < 0
    ? "up"
    : cpcChange.value > 0
    ? "down"
    : "neutral";
  const byCost = [...campaigns].sort((a, b) => b.costMinor - a.costMinor);
  // The pace chart stacks each campaign's clicks.
  const clickLayers = campaigns.map((c) => ({
    key: c.campaignId,
    name: c.name,
    startDate: c.startDate,
    daily: c.daily.map((d) => ({ date: d.date, value: d.clicks })),
  }));

  return (
    <ReportPanel
      title="Google Ads"
      icon={ICON.googleAds}
      summary={summary}
      headlineLabel="Clicks"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Clicks"
              value={cur.clicks.toLocaleString()}
              change={metricChange(cur.clicks, prev?.clicks)}
            />
          }
          chart={
            <Sparkline
              values={perDay(pace)}
              color={color}
              title="Clicks per day"
              spans={spans}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {!linked && <HistoryNote />}
        <PaceChart pace={pace} label="Clicks" color={color} {...frame} pips={pips} campaigns={clickLayers} />
        <ReportStats>
          <ReportStat
            label="Spend"
            value={formatEuroMinor(cur.costMinor)}
            change={metricChange(cur.costMinor, prev?.costMinor)}
            formatChange={formatMinorDelta}
          />
          <ReportStat
            label="Clicks"
            value={cur.clicks.toLocaleString()}
            change={metricChange(cur.clicks, prev?.clicks)}
          />
          <ReportStat
            label="Conversions"
            value={cur.conversions.toLocaleString()}
            change={metricChange(cur.conversions, prev?.conversions)}
          />
        </ReportStats>
        <QuietList
          rows={[
            quietRow(
              "Impressions",
              cur.impressions.toLocaleString(),
              metricChange(cur.impressions, prev?.impressions),
            ),
            quietRow(
              "CTR",
              cur.ctr != null ? `${(cur.ctr * 100).toFixed(2)}%` : "-",
              pointsChange(cur.ctr, prev?.ctr),
              formatPoints,
            ),
            quietRow(
              "CPC",
              cur.cpcMinor != null ? formatEuroMinor(cur.cpcMinor, true) : "-",
              cpcChange,
              formatMinorDelta,
              cpcTrend,
            ),
            quietRow(
              "Phone calls",
              cur.phoneCalls.toLocaleString(),
              metricChange(cur.phoneCalls, prev?.phoneCalls),
            ),
          ]}
        />
        {byCost.length > 0 && (
          <div>
            <VizHeading>Spend by campaign</VizHeading>
            <BarList
              color={color}
              rows={byCost.map((c) => ({
                key: c.campaignId,
                label: (
                  <>
                    {c.name}
                    {c.status && (
                      <span className="text-gray-400"> · {c.status.toLowerCase()}</span>
                    )}
                  </>
                ),
                value: c.costMinor,
                display: formatEuroMinor(c.costMinor),
              }))}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};

// --- Window mode ------------------------------------------------------------

/** The pace chart's line names in window mode: the subtitle names the days.
 *  Its label is short, "384 vs 362 before", so it fits a phone. */
const WINDOW_LABELS = { current: "these days", previous: "the days before", before: "before" };

/** The days of a source's window its data covers: none before `dataFrom`. */
const shownOf = (source: { span: DayRange | null; dataFrom: string }): DayRange | null =>
  source.span
    ? { since: source.span.since < source.dataFrom ? source.dataFrom : source.span.since, until: source.span.until }
    : null;

/** A window panel's subtitle: its own days against the days before, or, when
 *  the days before start before the data does, its days and why there is
 *  no change. An unlinked account says so first. */
const windowSummary = <T,>(source: GoogleSourceWindow<T>): string | undefined => {
  const shown = shownOf(source);
  const days = !shown
    ? null
    : source.compares && source.previousSpan
    ? formatRanges(shown, source.previousSpan)
    : `${formatRange(shown)} · no comparison: data starts ${formatShortDay(source.dataFrom)}`;
  if (!source.linked) return days ? `previously linked account · ${days}` : "previously linked account";
  return days ?? undefined;
};

/** A window source's view inputs: its figures against the days before
 *  (none when they are not compared), and its pace chart on its own days.
 *  Null while it has no figures in the window. The partial-month helpers
 *  never apply: a window is always whole days of settled data. */
const windowView = <T,>(
  source: GoogleSourceWindow<T>,
  campaigns: ReadonlyArray<CampaignSpan>,
  withLanes: boolean,
): SourceView<T> | null => {
  const shown = shownOf(source);
  if (!shown || !source.current) return null;
  const marks = campaignMarks(campaigns, { complete: true, pace: source.pace }, shown);
  return {
    linked: source.linked,
    summary: windowSummary(source),
    cur: source.current,
    prev: source.compares ? source.previous : null,
    pace: source.pace,
    spans: marks.spans,
    frame: {
      range: shown,
      previousRange: source.compares ? source.previousSpan : null,
      labels: WINDOW_LABELS,
      complete: true,
      toDate: null,
      ...(withLanes ? { lanes: marks.lanes } : {}),
    },
  };
};

/** A linked source with no figures in its window, or never fetched. */
const WindowNoRows: React.FC<{ source: GoogleSource; summary: string | undefined; linked: boolean }> = ({
  source,
  summary,
  linked,
}) => {
  const message = linked ? "No data for these days yet." : "No data for these days.";
  return (
    <ReportPanel
      title={TITLE[source]}
      icon={ICON[source]}
      summary={summary}
      collapsedBody={<ReportPanelNote>{message}</ReportPanelNote>}
    >
      <ReportPanelEmpty
        message={message}
        hint={
          linked
            ? "Rows appear once the daily Google sync covers these days."
            : "This panel shows history from a previously linked account; no new data will sync."
        }
      />
    </ReportPanel>
  );
};

const BusinessProfileWindowPanel: React.FC<{
  gbp: GoogleWindowReport["businessProfile"];
  campaigns: ReadonlyArray<CampaignSpan>;
}> = ({ gbp, campaigns }) => {
  if (!gbp) return <NotLinked source="businessProfile" />;
  const view = windowView(gbp, campaigns, true);
  if (!view) return <WindowNoRows source="businessProfile" summary={windowSummary(gbp)} linked={gbp.linked} />;
  return <BusinessProfileView {...view} keywords={null} />;
};

const WebsiteWindowPanel: React.FC<{
  website: GoogleWindowReport["website"];
  campaigns: ReadonlyArray<CampaignSpan>;
}> = ({ website, campaigns }) => {
  if (!website) return <NotLinked source="website" />;
  const view = windowView(website, campaigns, true);
  if (!view) return <WindowNoRows source="website" summary={windowSummary(website)} linked={website.linked} />;
  return <WebsiteView {...view} users={null} channels={website.channels} keyEvents={website.keyEvents} />;
};

const GoogleAdsWindowPanel: React.FC<{
  ads: GoogleWindowReport["googleAds"];
  campaigns: ReadonlyArray<CampaignSpan>;
  pips: ReadonlyArray<DayPip>;
}> = ({ ads, campaigns, pips }) => {
  if (!ads) return <NotLinked source="googleAds" />;
  const view = windowView(ads, campaigns, false);
  if (!view) return <WindowNoRows source="googleAds" summary={windowSummary(ads)} linked={ads.linked} />;
  return <GoogleAdsView {...view} campaigns={ads.campaigns} pips={pips} />;
};
